import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { yearOneTargets } from '@juvo/schemas';
import { store } from '../store.js';
import '../layouts/site-chrome.js';

@customElement('jv-about-page')
export class JvAboutPage extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .hero {
      padding: 2.5rem 0 2rem;
      border-bottom: var(--jv-border-w) solid var(--jv-color-border);
    }
    .kicker {
      font-family: var(--jv-font-mono);
      color: var(--jv-color-accent);
      font-size: var(--jv-fs-sm);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-bottom: 0.5rem;
    }
    h1 {
      font-size: 2.25rem;
      line-height: 1.15;
      margin: 0 0 1rem;
      color: var(--jv-color-text);
    }
    .subtitle {
      font-size: 1.15rem;
      color: var(--jv-color-muted);
      max-width: 48rem;
      line-height: 1.5;
      margin-bottom: 1.5rem;
    }
    .manifesto-box {
      background: var(--jv-color-surface);
      border-left: 4px solid var(--jv-color-accent);
      padding: 1.25rem 1.5rem;
      font-family: var(--jv-font-display);
      font-size: 1.2rem;
      line-height: 1.4;
      margin: 1.5rem 0;
      color: var(--jv-color-text);
    }
    .section-title {
      font-size: 1.5rem;
      margin: 2.5rem 0 1rem;
      padding-bottom: 0.5rem;
      border-bottom: var(--jv-border-w) solid var(--jv-color-border);
    }
    .pillars-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1.25rem;
      margin-top: 1.25rem;
    }
    @media (min-width: 640px) {
      .pillars-grid {
        grid-template-columns: 1fr 1fr;
      }
    }
    .pillar-card {
      background: var(--jv-color-surface);
      border: var(--jv-border-w) solid var(--jv-color-border);
      padding: 1.25rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .pillar-num {
      font-family: var(--jv-font-mono);
      font-size: 0.75rem;
      color: var(--jv-color-accent);
      text-transform: uppercase;
    }
    .pillar-name {
      font-size: 1.15rem;
      font-weight: 700;
      margin: 0;
    }
    .pillar-desc {
      color: var(--jv-color-muted);
      font-size: 0.95rem;
      line-height: 1.45;
      margin: 0;
    }
    .hub-box {
      margin-top: 2.5rem;
      padding: 1.5rem;
      background: var(--jv-color-surface);
      border: var(--jv-border-w) solid var(--jv-color-border);
      display: grid;
      gap: 1rem;
    }
    @media (min-width: 768px) {
      .hub-box {
        grid-template-columns: 1fr 1fr;
        align-items: center;
      }
    }
    .targets-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
      gap: 1rem;
      margin-top: 1.25rem;
    }
    .target-card {
      border: var(--jv-border-w) solid var(--jv-color-border);
      background: var(--jv-color-surface);
      padding: 1rem;
      text-align: center;
    }
    .target-val {
      font-family: var(--jv-font-display);
      font-size: 2rem;
      color: var(--jv-color-accent);
      margin: 0 0 0.25rem;
    }
    .target-lbl {
      font-size: 0.75rem;
      text-transform: uppercase;
      font-family: var(--jv-font-mono);
      color: var(--jv-color-muted);
      margin: 0;
    }
    .cta-row {
      margin-top: 2.5rem;
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;

  render() {
    const t = store.t.bind(store);
    return html`
      <jv-page>
        <div class="hero">
          <div class="kicker">// ${t('about.kicker')}</div>
          <h1>${t('about.title')}</h1>
          <p class="subtitle">${t('about.subtitle')}</p>
        </div>

        <div class="manifesto-box">
          “${t('about.manifesto')}”
        </div>

        <h2 class="section-title">${t('home.pillars')}</h2>
        <div class="pillars-grid">
          <div class="pillar-card">
            <span class="pillar-num">Pillar 01 // Circular</span>
            <h3 class="pillar-name">${t('about.pillar1Title')}</h3>
            <p class="pillar-desc">${t('about.pillar1Desc')}</p>
          </div>
          <div class="pillar-card">
            <span class="pillar-num">Pillar 02 // Academy</span>
            <h3 class="pillar-name">${t('about.pillar2Title')}</h3>
            <p class="pillar-desc">${t('about.pillar2Desc')}</p>
          </div>
          <div class="pillar-card">
            <span class="pillar-num">Pillar 03 // Currency</span>
            <h3 class="pillar-name">${t('about.pillar3Title')}</h3>
            <p class="pillar-desc">${t('about.pillar3Desc')}</p>
          </div>
          <div class="pillar-card">
            <span class="pillar-num">Pillar 04 // Platform</span>
            <h3 class="pillar-name">${t('about.pillar4Title')}</h3>
            <p class="pillar-desc">${t('about.pillar4Desc')}</p>
          </div>
        </div>

        <div class="hub-box">
          <div>
            <h3 style="margin: 0 0 0.5rem; font-size: 1.25rem;">${t('about.hubTitle')}</h3>
            <p class="pillar-desc">${t('about.hubDesc')}</p>
          </div>
          <div style="font-family: var(--jv-font-mono); font-size: 0.85rem; color: var(--jv-color-muted); border-left: 2px solid var(--jv-color-border); padding-left: 1rem;">
            <div>LOCATION: Faso Kanu, Bamako, Mali</div>
            <div>FOCUS: Primary Health Centers (CSComs)</div>
            <div>NETWORK: Juvo Geeks Community</div>
            <div>STATUS: Operational Workshop</div>
          </div>
        </div>

        <h2 class="section-title">${t('about.targetsTitle')}</h2>
        <div class="targets-grid">
          <div class="target-card">
            <p class="target-val">${yearOneTargets.kgRefurbished}kg</p>
            <p class="target-lbl">${t('impact.kg')}</p>
          </div>
          <div class="target-card">
            <p class="target-val">${yearOneTargets.cohortsRun}</p>
            <p class="target-lbl">${t('impact.cohorts')}</p>
          </div>
          <div class="target-card">
            <p class="target-val">${yearOneTargets.partnersActive}</p>
            <p class="target-lbl">${t('impact.partners')}</p>
          </div>
          <div class="target-card">
            <p class="target-val">${yearOneTargets.deployments}</p>
            <p class="target-lbl">${t('impact.deployments')}</p>
          </div>
        </div>

        <div class="cta-row">
          <jv-button href="/courses" variant="primary">${t('home.ctaCatalog')}</jv-button>
          <jv-button href="/geeks">${t('home.ctaGeek')}</jv-button>
          <jv-button href="/donate">${t('donate.now')}</jv-button>
        </div>
      </jv-page>
    `;
  }
}
